/**
 * Canvas Area Components
 * 
 * Reusable canvas wrapper components with consistent premium styling.
 */

import * as React from 'react'
import { Panel, useReactFlow } from 'reactflow'
import { useState, useEffect } from 'react'
import { Minus, Plus, Maximize2 } from 'lucide-react'
import { cn } from '../../lib/utils'

interface CanvasContainerProps {
  children: React.ReactNode
  className?: string
}

/**
 * CanvasContainer - Wrapper for ReactFlow canvas with proper sizing
 */
export function CanvasContainer({ children, className }: CanvasContainerProps) {
  return (
    <div className={cn('flex-1 relative', className)}>
      <div className="absolute inset-0">
        {children}
      </div>
    </div>
  )
}

/**
 * CanvasControls - Custom zoom/fit controls with clean, minimal styling
 */
export function CanvasControls() {
  const { zoomIn, zoomOut, fitView, getZoom } = useReactFlow()
  const [zoom, setZoom] = useState(100)

  useEffect(() => {
    const updateZoom = () => setZoom(Math.round(getZoom() * 100))
    updateZoom()
    const interval = setInterval(updateZoom, 100)
    return () => clearInterval(interval)
  }, [getZoom])

  return (
    <Panel position="bottom-left" className="ml-4 mb-4">
      <div className="bg-card border border-border rounded-lg flex flex-row items-center overflow-hidden">
        <button
          onClick={() => zoomOut()}
          className={cn(
            'p-2 hover:bg-muted transition-colors',
            'text-muted-foreground hover:text-foreground cursor-pointer'
          )}
          title="Zoom out"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <div
          className="px-2.5 py-2 text-xxs font-mono text-muted-foreground text-center min-w-[44px] border-x border-border select-none"
          title={`Current zoom: ${zoom}%`}
        >
          {zoom}%
        </div>
        <button
          onClick={() => zoomIn()}
          className={cn(
            'p-2 hover:bg-muted transition-colors',
            'text-muted-foreground hover:text-foreground cursor-pointer'
          )}
          title="Zoom in"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => fitView({ padding: 0.2, duration: 200 })}
          className={cn(
            'p-2 hover:bg-muted transition-colors border-l border-border',
            'text-muted-foreground hover:text-foreground cursor-pointer'
          )}
          title="Fit to view"
        >
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </Panel>
  )
}

interface LoadingStateProps {
  message?: string
}

/**
 * LoadingState - Loading indicator for workspace
 */
export function LoadingState({ message = 'Loading workspace...' }: LoadingStateProps) {
  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="flex flex-col items-center gap-4 animate-fade-in">
        <div className="w-8 h-8 border-2 border-border border-t-primary rounded-full animate-spin" />
        <div className="text-muted-foreground text-sm">{message}</div>
      </div>
    </div>
  )
}

interface ErrorStateProps {
  title?: string
  message?: string
}

/**
 * ErrorState - Error indicator for workspace
 */
export function ErrorState({
  title = 'Project not found',
  message = "The project you're looking for doesn't exist or you don't have access to it.",
}: ErrorStateProps) {
  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div className="flex flex-col items-center gap-3 text-center max-w-sm px-6 animate-fade-in">
        <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center">
          <svg
            className="w-6 h-6 text-muted-foreground"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
            />
          </svg>
        </div>
        <div>
          <h2 className="text-sm font-medium text-foreground mb-1">{title}</h2>
          <p className="text-xs text-muted-foreground">{message}</p>
        </div>
      </div>
    </div>
  )
}
